import * as THREE from 'three';
import { BundleGroup, PMREMGenerator, WebGPURenderer } from 'three/webgpu';
import { configureSharedGLTFLoader } from './SharedGLTFLoader';

export type RendererMode = 'webgl2' | 'webgpu';
export type RendererRequest = RendererMode | 'auto';
export type RendererBackendPreference = 'auto' | RendererMode;
export type AppRenderer = THREE.WebGLRenderer | WebGPURenderer;
export type WebGPUPMREMGeneratorCtor = new (renderer: WebGPURenderer) => {
  fromScene(scene: THREE.Scene, sigma?: number, near?: number, far?: number): { texture: THREE.Texture };
  dispose(): void;
};
/**
 * `BundleGroup` from `three/webgpu`. Children get recorded into a single
 * GPURenderBundle and replayed each frame as one CPU-cheap draw call. Three
 * exports it from the WebGPU module only, so we surface the constructor via
 * the renderer handle instead of importing `three/webgpu` at each call site.
 * The material stack already imports WebGPU node APIs statically, so keeping
 * this import explicit avoids a misleading dynamic-import split point.
 */
export type WebGPUBundleGroupCtor = new () => THREE.Group & {
  readonly isBundleGroup: true;
  static: boolean;
  needsUpdate: boolean;
};

export interface RendererCapabilities {
  postProcessing: boolean;
  webglTimerQuery: boolean;
  preserveDrawingBuffer: boolean;
  webgpuTimestamps: boolean;
}

interface RendererHandleBase {
  renderer: AppRenderer;
  mode: RendererMode;
  requestedMode: RendererRequest;
  isWebGPU: boolean;
  webglRenderer: THREE.WebGLRenderer | null;
  webgpuRenderer: WebGPURenderer | null;
  webgpuPMREMGeneratorCtor: WebGPUPMREMGeneratorCtor | null;
  webgpuBundleGroupCtor: WebGPUBundleGroupCtor | null;
  fallbackReason: string | null;
  capabilities: RendererCapabilities;
}

export interface WebGLRendererHandle extends RendererHandleBase {
  renderer: THREE.WebGLRenderer;
  mode: 'webgl2';
  isWebGPU: false;
  webglRenderer: THREE.WebGLRenderer;
  webgpuRenderer: null;
  webgpuPMREMGeneratorCtor: null;
  webgpuBundleGroupCtor: null;
}

export interface WebGPURendererHandle extends RendererHandleBase {
  renderer: WebGPURenderer;
  mode: 'webgpu';
  requestedMode: 'webgpu' | 'auto';
  isWebGPU: true;
  webglRenderer: null;
  webgpuRenderer: WebGPURenderer;
  webgpuPMREMGeneratorCtor: WebGPUPMREMGeneratorCtor;
  webgpuBundleGroupCtor: WebGPUBundleGroupCtor;
  fallbackReason: null;
}

export type RendererHandle = WebGLRendererHandle | WebGPURendererHandle;

interface CreateRendererOptions {
  /**
   * Used by WebGL-only review tools until their PMREM/post stack is ported.
   * Main gameplay leaves this false so ?webgpu exercises the real path.
   */
  forceWebGL?: boolean;
}

interface WebGPUAdapterLike {}

interface NavigatorGpu {
  requestAdapter?: (options?: { powerPreference?: 'low-power' | 'high-performance' }) => Promise<WebGPUAdapterLike | null>;
}

const RENDERER_BACKEND_STORAGE_KEY = 'tst:renderer-backend:v1';

/**
 * Cap the WebGL pixel ratio. Racing responsiveness matters more than retina
 * backbuffer sharpness: 2x DPR is 4x the fragment cost of 1x. Empirically,
 * for the canyon scene at 2x device DPR (1M tris with aerial-perspective +
 * value-composition fragment work + 8-pass postfx pipeline), dropping the
 * cap from 1.5 → 1.0 took FPS from 38 → 60 with no visible quality loss in
 * motion. `?dpr=1.5` opts back into the sharper cap for screenshots.
 */
export function resolvePixelRatio(devicePixelRatio: number): number {
  if (!Number.isFinite(devicePixelRatio) || devicePixelRatio <= 0) return 1;
  const override = readDprOverride();
  if (override !== null) return Math.min(devicePixelRatio, override);
  return Math.min(devicePixelRatio, 1.0);
}

export function resolveWebGPUMsaaEnabled(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): boolean {
  const params = new URLSearchParams(search);
  if (!params.has('aa')) return false;
  const value = params.get('aa');
  return value !== '0' && value !== 'false' && value !== 'off';
}

function readDprOverride(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('dpr');
  if (!raw || raw === 'auto') return null;
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') return uaData.mobile;
  return /Mobi/i.test(navigator.userAgent ?? '');
}

export function isAndroidUserAgent(userAgent: string): boolean {
  return /Android/i.test(userAgent);
}

export function isAndroidDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return isAndroidUserAgent(navigator.userAgent ?? '');
}

export function readRendererBackendPreference(storage: Storage | null = safeLocalStorage()): RendererBackendPreference {
  if (!storage) return 'auto';
  try {
    const raw = storage.getItem(RENDERER_BACKEND_STORAGE_KEY);
    return raw === 'webgl2' || raw === 'webgpu' || raw === 'auto' ? raw : 'auto';
  } catch {
    return 'auto';
  }
}

export function setRendererBackendPreference(value: RendererBackendPreference, storage: Storage | null = safeLocalStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(RENDERER_BACKEND_STORAGE_KEY, value);
  } catch {
    // localStorage may be unavailable or full; the URL params still work.
  }
}

export function resolveRendererRequest(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  storage: Storage | null = safeLocalStorage(),
): RendererRequest {
  const params = new URLSearchParams(search);
  if (params.has('webgl')) return 'webgl2';
  if (params.has('webgpu')) return 'webgpu';
  return readRendererBackendPreference(storage);
}

export function shouldAttemptWebGPU(requestedMode: RendererRequest, mobile = isMobileDevice()): boolean {
  if (requestedMode === 'webgl2') return false;
  // Phase F hardware sign-off gates automatic mobile promotion. Explicit
  // requests still go through so QA can test iOS/Android WebGPU builds.
  if (requestedMode === 'auto' && mobile) return false;
  return true;
}

export function isWebGLRenderer(renderer: AppRenderer): renderer is THREE.WebGLRenderer {
  return (renderer as THREE.WebGLRenderer & { isWebGLRenderer?: boolean }).isWebGLRenderer === true;
}

export function isWebGPURenderer(renderer: AppRenderer): renderer is WebGPURenderer {
  return (renderer as WebGPURenderer & { isWebGPURenderer?: boolean }).isWebGPURenderer === true;
}

/** WebGPU by default on capable browsers; ?webgl opts out to WebGL2. */
export function createRenderer(canvasParent: HTMLElement, options: CreateRendererOptions & { forceWebGL: true }): Promise<WebGLRendererHandle>;
export function createRenderer(canvasParent: HTMLElement, options?: CreateRendererOptions): Promise<RendererHandle>;
export async function createRenderer(canvasParent: HTMLElement, options: CreateRendererOptions = {}): Promise<RendererHandle> {
  const requestedMode = options.forceWebGL ? 'webgl2' : resolveRendererRequest();
  const mobile = isMobileDevice();
  let fallbackReason: string | null = requestedMode !== 'webgl2' ? 'webgpu-unavailable' : null;

  if (requestedMode !== 'webgl2' && shouldAttemptWebGPU(requestedMode, mobile)) {
    try {
      const handle = await tryCreateWebGPU(canvasParent, requestedMode);
      if (handle) return handle;
      console.warn('[renderer] WebGPU unavailable; falling back to WebGL2');
    } catch (err) {
      console.warn('[renderer] WebGPU init failed; falling back to WebGL2', err);
    }
  } else if (requestedMode === 'auto' && mobile) {
    fallbackReason = 'webgpu-mobile-deferred';
  }

  return createWebGL2(canvasParent, requestedMode, fallbackReason);
}

function createWebGL2(
  canvasParent: HTMLElement,
  requestedMode: RendererRequest = 'webgl2',
  fallbackReason: string | null = null,
): WebGLRendererHandle {
  // ?preserveDrawingBuffer=1 keeps the WebGL backbuffer readable after each
  // frame so test harnesses (slider-sweep) can hash the canvas pixels via
  // canvas.toDataURL/getContext('webgl2').readPixels without a Playwright
  // IPC roundtrip. Costs perf so it's opt-in via URL flag — never on in
  // production gameplay.
  const params = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
  const preserveBuffer = params.has('preserveDrawingBuffer');
  // Post-fx renders through EffectComposer targets, so canvas MSAA is wasted
  // in normal gameplay. Keep an explicit opt-in for visual captures.
  const antialias = params.has('aa');
  const renderer = new THREE.WebGLRenderer({
    antialias,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: preserveBuffer,
  });
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  renderer.setPixelRatio(resolvePixelRatio(dpr));
  renderer.setSize(canvasParent.clientWidth, canvasParent.clientHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.98;
  renderer.shadowMap.enabled = true;
  // r184 removed the separate PCFSoft mode and maps it to PCF with a warning;
  // use the live enum directly so the WebGL fallback boots cleanly.
  renderer.shadowMap.type = THREE.PCFShadowMap;
  // Auto-reset wipes draw call / triangle counters on every renderer.render()
  // call, which means PerfHud only sees stats from the LAST pass (a single
  // full-screen blit) when post-fx is on. We reset manually once per frame
  // in the main render callback so the HUD shows true cumulative totals.
  renderer.info.autoReset = false;
  canvasParent.appendChild(renderer.domElement);
  configureSharedGLTFLoader(renderer);
  return {
    renderer,
    mode: 'webgl2',
    requestedMode,
    isWebGPU: false,
    webglRenderer: renderer,
    webgpuRenderer: null,
    webgpuPMREMGeneratorCtor: null,
    webgpuBundleGroupCtor: null,
    fallbackReason,
    capabilities: {
      postProcessing: true,
      webglTimerQuery: true,
      preserveDrawingBuffer: preserveBuffer,
      webgpuTimestamps: false,
    },
  };
}

async function tryCreateWebGPU(
  canvasParent: HTMLElement,
  requestedMode: Exclude<RendererRequest, 'webgl2'>,
): Promise<WebGPURendererHandle | null> {
  const params = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
  const gpu = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { gpu?: NavigatorGpu }).gpu
    : undefined;
  if (!gpu?.requestAdapter) return null;

  const adapter = await gpu.requestAdapter({ powerPreference: 'high-performance' });
  if (!adapter) return null;

  // Post-FX uses WebGPU render targets whose sample count follows the
  // renderer's MSAA setting. Keep MSAA opt-in, matching WebGL, because the
  // post stack is already full-screen fill-rate heavy.
  const antialias = resolveWebGPUMsaaEnabled(params.toString());
  const renderer = new WebGPURenderer({
    antialias,
    powerPreference: 'high-performance',
    trackTimestamp: true,
  });
  configureCommonRenderer(renderer, canvasParent);

  await renderer.init();
  renderer.setClearColor(0x000000, 1);
  if (!(renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend) {
    renderer.dispose();
    return null;
  }

  installWebGPUDeviceLostEvent(renderer);
  const webgpuTimestamps = hasWebGPUFeature(renderer, 'timestamp-query');
  canvasParent.appendChild(renderer.domElement);
  configureSharedGLTFLoader(renderer);
  return {
    renderer,
    mode: 'webgpu',
    requestedMode,
    isWebGPU: true,
    webglRenderer: null,
    webgpuRenderer: renderer,
    webgpuPMREMGeneratorCtor: PMREMGenerator,
    webgpuBundleGroupCtor: BundleGroup as unknown as WebGPUBundleGroupCtor,
    fallbackReason: null,
    capabilities: {
      postProcessing: true,
      webglTimerQuery: false,
      preserveDrawingBuffer: false,
      webgpuTimestamps,
    },
  };
}

function configureCommonRenderer(renderer: AppRenderer, canvasParent: HTMLElement): void {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  renderer.setPixelRatio(resolvePixelRatio(dpr));
  renderer.setSize(canvasParent.clientWidth, canvasParent.clientHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.98;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.info.autoReset = false;
}

function hasWebGPUFeature(renderer: WebGPURenderer, feature: string): boolean {
  try {
    return renderer.hasFeature(feature);
  } catch {
    return false;
  }
}

function installWebGPUDeviceLostEvent(renderer: WebGPURenderer): void {
  const target = renderer as WebGPURenderer & {
    onDeviceLost?: (info: unknown) => void;
  };
  const previous = target.onDeviceLost?.bind(renderer);
  target.onDeviceLost = (info: unknown) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tst:webgpu-device-lost', { detail: info }));
    }
    previous?.(info);
  };
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
